import { useEffect, useMemo, useState } from "react";
import Button from "../ui/Button.jsx";
import StatusBadge from "./StatusBadge.jsx";
import { useToast } from "../../hooks/useToast.js";
import { formatDateTime } from "../../utils/adminUserFormat.js";
import { mapAdminError, readApiError } from "../../utils/adminApiErrors.js";
import { buildPatch, draftFromUser, isDraftDirty } from "./adminUserPatch.js";

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "var(--text-secondary)",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid var(--border-bright)",
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

function FieldWithHint({ label, hint, children }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
      {hint ? (
        <p className="text-secondary" style={{ fontSize: "12px", margin: "6px 0 0", lineHeight: 1.5 }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function DisabledWrap({ disabled, title, children }) {
  if (!disabled) return children;
  return (
    <span title={title} style={{ display: "block", cursor: "not-allowed" }}>
      <span style={{ pointerEvents: "none", opacity: 0.55, display: "block" }}>{children}</span>
    </span>
  );
}

function ReadOnlyRow({ label, value }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: "14px" }}>{value}</div>
    </div>
  );
}

export default function UserDetailProfileTab({
  user,
  actorId,
  onDirtyChange,
  onSaved,
  dangerZone,
}) {
  const { show: showToast } = useToast();
  const [baseline, setBaseline] = useState(() => draftFromUser(user));
  const [draft, setDraft] = useState(() => draftFromUser(user));
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const next = draftFromUser(user);
    setBaseline(next);
    setDraft(next);
  }, [user]);

  const dirty = useMemo(() => isDraftDirty(draft, baseline), [draft, baseline]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const status = String(user?.status || "").toLowerCase();
  const isOwner = Boolean(user?.is_owner);
  const isSelf = actorId && user?.id && actorId === user.id;
  const statusEditable = status === "active" || status === "suspended";

  const roleDisabled = isOwner || (isSelf && baseline.isAdmin);
  const roleTooltip = isOwner
    ? "Owner role can only be changed via Transfer ownership"
    : isSelf
      ? "You cannot demote yourself"
      : "";

  const statusDisabled = isOwner || isSelf || !statusEditable;
  const statusTooltip = isOwner
    ? "Owner cannot be suspended"
    : isSelf
      ? "You cannot suspend yourself"
      : !statusEditable
        ? "Status can only be changed for active or suspended accounts"
        : "";

  const emailDisabled = isOwner;
  const emailTooltip = isOwner ? "Owner email cannot be changed here" : "";

  const passwordResetDisabled = status === "deactivated" || status === "deleted";
  const passwordResetTooltip =
    status === "deactivated" ? "User is deactivated" : status === "deleted" ? "User is deleted" : "";

  function updateDraft(partial) {
    setDraft((prev) => ({ ...prev, ...partial }));
  }

  async function handleSave(e) {
    e.preventDefault();
    const patch = buildPatch(draft, baseline);
    if (!Object.keys(patch).length) return;

    setSaving(true);
    try {
      const res = await fetch(`/api/admin/users/${encodeURIComponent(user.id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const msg = await readApiError(res);
        showToast(msg, "error");
        return;
      }
      const updated = await res.json();
      showToast("User updated", "success");
      onSaved?.(updated);
    } catch {
      showToast(mapAdminError(), "error");
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordReset() {
    setResetting(true);
    try {
      const res = await fetch(
        `/api/admin/users/${encodeURIComponent(user.id)}/password-reset`,
        { method: "POST", credentials: "include" },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(mapAdminError(body?.error) || "Failed to send password reset.", "error");
        return;
      }
      showToast(body?.message || "Recovery email sent", "info");
    } catch {
      showToast("Failed to send password reset. Try again.", "error");
    } finally {
      setResetting(false);
    }
  }

  return (
    <form onSubmit={handleSave}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        <FieldWithHint label="First name">
          <input
            style={inputStyle}
            value={draft.firstName}
            onChange={(e) => updateDraft({ firstName: e.target.value })}
          />
        </FieldWithHint>
        <FieldWithHint label="Last name">
          <input
            style={inputStyle}
            value={draft.lastName}
            onChange={(e) => updateDraft({ lastName: e.target.value })}
          />
        </FieldWithHint>
      </div>

      <FieldWithHint
        label="Email"
        hint={
          !emailDisabled
            ? "Changing email updates the user's login identity. They will receive a notification at the new email."
            : undefined
        }
      >
        <DisabledWrap disabled={emailDisabled} title={emailTooltip}>
          <input
            type="email"
            style={inputStyle}
            value={draft.email}
            disabled={emailDisabled}
            onChange={(e) => updateDraft({ email: e.target.value })}
          />
        </DisabledWrap>
      </FieldWithHint>

      {!isOwner ? (
        <FieldWithHint label="Role">
          <DisabledWrap disabled={roleDisabled} title={roleTooltip}>
            <div style={{ display: "flex", gap: "16px", fontSize: "14px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: roleDisabled ? "not-allowed" : "pointer" }}>
                <input
                  type="radio"
                  name="role"
                  checked={!draft.isAdmin}
                  disabled={roleDisabled}
                  onChange={() => updateDraft({ isAdmin: false })}
                />
                User
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: roleDisabled ? "not-allowed" : "pointer" }}>
                <input
                  type="radio"
                  name="role"
                  checked={draft.isAdmin}
                  disabled={roleDisabled}
                  onChange={() => updateDraft({ isAdmin: true })}
                />
                Admin
              </label>
            </div>
          </DisabledWrap>
        </FieldWithHint>
      ) : null}

      <FieldWithHint label="Status">
        {statusEditable ? (
          <DisabledWrap disabled={statusDisabled} title={statusTooltip}>
            <select
              style={inputStyle}
              value={draft.status}
              disabled={statusDisabled}
              onChange={(e) => updateDraft({ status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </DisabledWrap>
        ) : (
          <div>
            <StatusBadge status={user.status} />
          </div>
        )}
      </FieldWithHint>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "20px",
          padding: "12px 0",
          borderTop: "1px solid var(--border)",
        }}
      >
        <ReadOnlyRow label="Created" value={formatDateTime(user.created_at)} />
        <ReadOnlyRow label="Updated" value={formatDateTime(user.updated_at)} />
        <ReadOnlyRow label="Last activity" value={formatDateTime(user.last_activity)} />
      </div>

      <div style={{ marginBottom: "20px" }}>
        <DisabledWrap disabled={passwordResetDisabled} title={passwordResetTooltip}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={resetting}
            disabled={passwordResetDisabled}
            onClick={handlePasswordReset}
          >
            Send password reset
          </Button>
        </DisabledWrap>
      </div>

      <div
        style={{
          marginBottom: "20px",
          padding: "12px 14px",
          borderRadius: "6px",
          border: "1px solid var(--border)",
          background: "var(--bg-elevated)",
        }}
      >
        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px" }}>
          Danger zone
        </div>
        {dangerZone?.showTransferOwnership ? (
          <div style={{ marginBottom: "10px" }}>
            <Button type="button" variant="secondary" size="sm" onClick={dangerZone.onTransferOwnership}>
              Transfer ownership
            </Button>
          </div>
        ) : null}
        {dangerZone?.status === "active" || dangerZone?.status === "suspended" ? (
          <>
            <span title={dangerZone.deactivateTooltip || ""}>
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={dangerZone.deactivateDisabled}
                onClick={dangerZone.onDeactivate}
              >
                Deactivate user
              </Button>
            </span>
          </>
        ) : null}
        {dangerZone?.status === "deactivated" ? (
          <>
            <span title={dangerZone.deleteTooltip || ""}>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={dangerZone.onDelete}
                disabled={dangerZone.deleteDisabled}
              >
              Delete user (soft)
              </Button>
            </span>
            <p className="text-secondary" style={{ fontSize: "12px", margin: "8px 0 0", lineHeight: 1.5 }}>
              This user is deactivated. Deletion is permanent.
            </p>
          </>
        ) : null}
        {dangerZone?.status === "deleted" ? (
          <p className="text-secondary" style={{ fontSize: "13px", margin: 0, lineHeight: 1.5 }}>
            This user has been deleted.
          </p>
        ) : null}
      </div>

      <Button type="submit" loading={saving} disabled={!dirty || saving}>
        Save changes
      </Button>
    </form>
  );
}
